import {
  forwardRef,
  type AriaAttributes,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'

import { cn } from '../cn'
import { useFieldContext } from './form-field'

export const controlClass =
  'w-full rounded-sm border border-line-strong bg-panel px-3 text-[13.5px] text-ink placeholder:text-muted-2 transition-colors ' +
  'hover:border-muted-2 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25 ' +
  'disabled:cursor-not-allowed disabled:bg-paper disabled:text-muted aria-invalid:border-lost aria-invalid:focus:ring-lost/25'

function useControlProps(props: {
  id?: string
  'aria-invalid'?: AriaAttributes['aria-invalid']
  required?: boolean
}) {
  const field = useFieldContext()
  return {
    id: props.id ?? field?.id,
    'aria-describedby': field?.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (field?.invalid ? true : undefined),
    required: props.required ?? field?.required,
  }
}

export type InputProps = InputHTMLAttributes<HTMLInputElement>

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ...props },
  ref,
) {
  const wired = useControlProps(props)
  return <input ref={ref} {...props} {...wired} className={cn(controlClass, 'h-9', className)} />
})

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 3, ...props },
  ref,
) {
  const wired = useControlProps(props)
  return (
    <textarea
      ref={ref}
      rows={rows}
      {...props}
      {...wired}
      className={cn(controlClass, 'py-2 leading-relaxed', className)}
    />
  )
})

export interface SelectOption {
  readonly value: string
  readonly label: string
  readonly disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  readonly options: readonly SelectOption[]
  readonly placeholder?: string
}

/** A native select — reliable on mobile, fully keyboard accessible, styled to match Input. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, options, placeholder, ...props },
  ref,
) {
  const wired = useControlProps(props)
  return (
    <select
      ref={ref}
      {...props}
      {...wired}
      className={cn(
        controlClass,
        'h-9 appearance-none bg-[url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%235B6B85%27 stroke-width=%272%27%3E%3Cpath d=%27m6 9 6 6 6-6%27/%3E%3C/svg%3E")] bg-[length:16px] bg-[right_10px_center] bg-no-repeat pr-9',
        className,
      )}
    >
      {placeholder !== undefined && (
        <option value="" disabled={props.required}>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  )
})
